import React, { useMemo, useEffect } from 'react';
import { useSelector, useIntl, getLocale,useLocation  } from 'umi';
import Filter from '../../components/PCStoreRankMoreFilter';
import ExportTable from '@/pages/intelligence/components/ExportTable';
import StoreRankMoreCore from '../../../components/StoreRankMoreCore';
import useStoreTopRegionOptionsMap from '@intelligence/v3/_hooks/useStoreTopRegionOptionsMap';
import useFilterExtend from '@/pages/intelligence/hooks/useStoreRankFilterExtend';
import useInitialTopChartsName from '@/pages/intelligence/pages/StoreRanks/components/StoreRankMoreCore/hooks/useInitialTopChartsName';
import useBuriedPointLogCfgKeys from '@/pages/intelligence/pages/StoreRanks/components/StoreRankMoreCore/hooks/useBuriedPointLogCfgKeys';
import useStoreRankMoreCore from '@/pages/intelligence/pages/StoreRanks/components/StoreRankMoreCore/hooks/useStoreRankMoreCore';
import { handleRangeDate2SingleDateRangeTimeFilterExtend } from '@intelligence/utils/dateUtils';
import { FILTER_EXTEND_NAMESPACE } from '@/pages/intelligence/models/storeRankExtend';
import * as FILTER_KEYS from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { LANGUAGE_CN } from '@intelligence/_config/config';
import {
  PRIMARY_KEY,
  PC_UNIFIED_KEY,
  PC_SOURCES,
  PC_CONSOLE_SECTIONAL_RANK_KEYS,
  PC_CONSOLE_SECTIONAL_RANK_INFOS,
  PC_SOURCES_METRICS_KEYS,
  PC_PLATFORM_TOP_INFOS,
  PC_SOURCE_STEAM_TOP_SELLERS,
} from '@/pages/intelligence/_config/storeRank/config';
import { PC_ENTITY_TYPE, STEAM_PLATFORM, PC_PLATFORMS } from '@intelligence/v3/constants/topcharts';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';

import type { IBaseProps } from '@/pages/intelligence/types/common';
import type { IStoreRankMoreCoreFetchParams, IStoreRankMoreCoreRank } from '@/pages/intelligence/types/storeRank';

const SEARCH_TYPE = 'pc-store-rank-more';
const TOP_CHARTS_TYPE = 'store-rank-more';
const BUSINESS_TYPE = `${PC_ENTITY_TYPE}-${TOP_CHARTS_TYPE}`;
const DEFAULT_FETCH_PARAMS: IStoreRankMoreCoreFetchParams = {
  entity_type: PC_ENTITY_TYPE,
  topchart_name: PC_SOURCE_STEAM_TOP_SELLERS,
  region_type: 'market',
  regions: ['global'],
  genre: [],
  sub_genre: [],
  genre_source: 'iegg',
  iegg_genre_req: [],
  granularity: 'daily',
  start_date: '',
  end_date: '',
  start_release_time: '',
  end_release_time: '',
  order_source: '',
  order_metric: 'rank',
  order: 'desc',
  ratio_type: 0,
  page: 1,
  page_size: 100,
  search_ids: [],
  search_by_name: '',
};

type IPCStoreRankMoreProps = IBaseProps;

const PCStoreRankMore: React.FC<IPCStoreRankMoreProps> = (props) => {
  const { } = props;
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isCN = locale === LANGUAGE_CN;
  const filterState = useSelector((rootState: any) => rootState?.[FILTER_EXTEND_NAMESPACE]) ?? {};
  const isOperate = !!filterState?.isOperate;
  const { query: pageQuerys }:any = useLocation()

  const { filterExtend } = useFilterExtend({});

  const initialTopChartsName = useInitialTopChartsName(PC_SOURCES);
  const initialFetchParams = useMemo<IStoreRankMoreCoreFetchParams>(() => {
    const defaultFetchParams = {
      ...DEFAULT_FETCH_PARAMS,
      granularity: pageQuerys?.granularity || 'daily',
      start_date: pageQuerys?.start_date || DEFAULT_FETCH_PARAMS.start_date,
      end_date: pageQuerys?.end_date || DEFAULT_FETCH_PARAMS.end_date,
      topchart_name: initialTopChartsName,
    };
    if (!isOperate) return defaultFetchParams;

    const filterExtendTmp = handleRangeDate2SingleDateRangeTimeFilterExtend(filterExtend);
    const timeExtend = filterExtendTmp?.[FILTER_KEYS.RANGE_TIME];
    const regionExtend = filterExtendTmp?.[FILTER_KEYS.REGION];
    const initialGranularity = timeExtend?.granularity || DEFAULT_FETCH_PARAMS.granularity;
    const initialStartDate = timeExtend?.start_date || DEFAULT_FETCH_PARAMS.start_date;
    const initialEndDate = timeExtend?.end_date || DEFAULT_FETCH_PARAMS.end_date;
    const initialRegions =
      initialTopChartsName === PC_SOURCE_STEAM_TOP_SELLERS && regionExtend?.markets?.length > 0
        ? regionExtend?.markets
        : DEFAULT_FETCH_PARAMS.regions;

    return {
      ...defaultFetchParams,
      regions: initialRegions,
      granularity: initialGranularity,
      start_date: initialStartDate,
      end_date: initialEndDate,
    };
  }, [isOperate, initialTopChartsName, filterExtend]);

  const { pageLogCfg, downloadExcelLogCfgKey } = useBuriedPointLogCfgKeys(PC_ENTITY_TYPE, initialTopChartsName);

  const {
    fetchParams,
    isSectional,
    platform,
    platformName,
    topName,

    handleInit,
    handleConfirm,
    handleReset,

    isVisualisLoading,
    isVisualisNoData,
    seriesData,
    xAxisData,
    sortKeyName,
    currentLine,
    segments,
    segment,
    setSegment,
    yAxisFormatter,
    tooltipFormatter,
    createLegendsCustomMade,

    isTableLoading,
    isTableNoData,
    tableDimensionsData,
    tableTotal,
    tableData,
    tableSelection,
    defaultSelectionGames,
    transTableDataForSearch,
    getTableSearchList,
    handleClearSearch,
    onTableSortChage,
    onTablePagingChange,
    onTableCheckGame,
    onPageInit,
  } = useStoreRankMoreCore({
    entityType: PC_ENTITY_TYPE,
    pageLogCfg,
    primaryKey: PRIMARY_KEY,
    platformTopMetrics: PC_SOURCES_METRICS_KEYS,
    initialFetchParams,
    getIsSectional: (fetchParams: IStoreRankMoreCoreFetchParams) => {
      const flag1 = fetchParams?.topchart_name === PC_SOURCE_STEAM_TOP_SELLERS;
      const flag2 = fetchParams?.regions?.length > 1;
      return flag1 && flag2;
    },
  });

  const { regionsOptions: regionOptions } = useStoreTopRegionOptionsMap(STEAM_PLATFORM, {
    entityType: PC_ENTITY_TYPE,
    platforms: PC_PLATFORMS,
  });

  useEffect(() => {
    onPageInit(fetchParams);
  }, []);

  return (
    <>
      <MarqueeBanner />
      <StoreRankMoreCore
        entityType={PC_ENTITY_TYPE}
      searchType={SEARCH_TYPE}
      pageLogInfo={pageLogCfg}
      title={`${platformName} ${topName}`}
      isOperate={isOperate}
      isSectional={isSectional}
      isShowVisualis={true}
      fetchParams={fetchParams}
      visualisOptions={{
        isVisualisLoading,
        isVisualisNoData,
        seriesData,
        xAxisData,
        sortKeyName,
        currentLine,
        segments,
        segment,
        setSegment,
        yAxisFormatter,
        tooltipFormatter,
        createLegendsCustomMade,
      }}
      tablesOptions={{
        isTableLoading,
        isTableNoData,
        tableTotal,
        // 表格数据的主键，表格的recordKey、以及分段排名rank模式时，可视化需要用到
        primaryKey: PRIMARY_KEY,
        uunifiedKey: PC_UNIFIED_KEY,
        imageKey: 'cover',
        sectionalRankKeys: PC_CONSOLE_SECTIONAL_RANK_KEYS,
        sectionalRankInfos: PC_CONSOLE_SECTIONAL_RANK_INFOS,
        platformTopMetrics: PC_SOURCES_METRICS_KEYS,
        platformTopInfos: PC_PLATFORM_TOP_INFOS,
        tableDimensionsData,
        tableData,
        tableSelection,
        defaultSelectionGames,
        getDetailsBtnDisabledInfo: (cvm: IStoreRankMoreCoreRank) => {
          const platforms = cvm?.platform;
          const disabled = platforms?.length === 1 && platforms?.[0] === 'device';
          return {
            disabled,
            disabledMsg: formatMessage({ id: 'dbi_store_rank_pc_no_provide_game_details_tips' }) as string,
          };
        },
        transTableDataForSearch,
        getTableSearchList,
        handleClearSearch,
        onTableSortChage,
        onTablePagingChange,
        onTableCheckGame,
      }}
      filter={
        <Filter
          isOperate={isOperate}
          buttonLogConfig={{}}
          granularity={fetchParams?.granularity}
          startDate={fetchParams?.start_date}
          endDate={fetchParams?.end_date}
          showRegionSelect={fetchParams?.topchart_name === PC_SOURCE_STEAM_TOP_SELLERS}
          regionOptions={regionOptions}
          onInit={handleInit}
          onConfirm={handleConfirm}
          onReset={handleReset}
        />
      }
      exportTable={
        <ExportTable
          entityType={PC_ENTITY_TYPE}
          // @ts-ignore
          businessType={BUSINESS_TYPE}
          downloadLogConfigKey={downloadExcelLogCfgKey}
          isDisableDownload={isTableLoading}
          downloadModalSize={isCN ? 604 : 684}
          tableParams={{
            entity_type: PC_ENTITY_TYPE,
            app_ids: [],
            selectedIdType: isSectional ? 'unified_id' : 'app_id',
            select_ids: tableSelection,
            gameCode: [],
            store: platform,
            source: fetchParams?.topchart_name,
            metrics: ['1-3', '1-10', '1-50', '1-200'],
            order_source: fetchParams?.order_source,
            order: fetchParams?.order,
            order_metric: fetchParams?.order_metric,
            region_type: fetchParams?.region_type,
            regions: fetchParams?.regions,
            granularity: fetchParams?.granularity,
            start_date: fetchParams?.start_date,
            end_date: fetchParams?.end_date,
            filter: {
              start_release_date: fetchParams?.start_release_time,
              end_release_date: fetchParams?.end_release_time,
            },
            page: 1,
            page_size: 100,
          }}
          total={tableTotal}
          maxSize={tableTotal}
          isSyncPageSizeWithSelectTop={true}
          useDownloadAmountType={true}
          texts={{ top: `${platformName} ${topName} ${formatMessage({ id: 'dbi_rank' })}` }}
          isNeedCheckedLines={true}
          getCheckedLinesCfg={() => {
            const primaryKey = isSectional ? 'unified_id' : 'app_id';
            const ids: string[] = tableSelection?.map((item) => item?.[primaryKey]) || [];
            return { key: 'app_ids', value: ids };
          }}
        />
      }
    />
    </>
  );
};

export default PCStoreRankMore;
